// Shared persistence for saved maps. Every tool funnels through this — the only
// per-tool code is a small getSnapshot()/applySnapshot() adapter. Nothing here
// knows what a "shaper" or a "trail" is.
//
// Layout:
//   Firestore  users/{uid}/maps/{id}   → { type, name, breadcrumb, settings, *Path, thumbUrl, timestamps }
//   Storage    userMaps/{uid}/{id}/source       → heavy source asset (e.g. the shaper photo)
//   Storage    userMaps/{uid}/{id}/payload.json → heavy JSON payload (e.g. trail GPS tracks)
//   Storage    userMaps/{uid}/{id}/thumb.jpg    → list thumbnail
//
// `settings` is small JSON (colors, toggles, units, selected shape/region, road-
// trip stops…) and lives inline in the doc. Anything large goes to Storage so we
// never hit Firestore's 1 MB/doc limit.

import { auth, db, storage } from '../firebaseApp';
import {
    collection,
    doc,
    setDoc,
    getDoc,
    getDocs,
    deleteDoc,
    query,
    orderBy,
    serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, getBlob, deleteObject } from 'firebase/storage';
import { blobToDataUrl } from './blob';

const requireUid = () => {
    const u = auth.currentUser;
    if (!u || u.isAnonymous) {
        throw new Error('You need to be signed in to save maps.');
    }
    return u.uid;
};

const mapsCol = (uid) => collection(db, 'users', uid, 'maps');
const assetRef = (uid, id, name) => ref(storage, `userMaps/${uid}/${id}/${name}`);

/**
 * A snapshot (produced by a tool's getSnapshot()) has the shape:
 *   {
 *     type: 'shaper' | 'travel' | 'trail' | 'roadtrip',
 *     defaultName: string,            // suggested name for a brand-new save
 *     breadcrumb: string[],           // e.g. ['Shaped','Countries','France']
 *     settings: object,               // small JSON, restored verbatim
 *     assets?: { source?: Blob, payload?: Blob },  // heavy data → Storage
 *     thumbnailBlob?: Blob,           // list preview
 *   }
 */

async function writeAssets(uid, id, snapshot) {
    const out = { sourcePath: null, payloadPath: null, thumbPath: null, thumbUrl: null };

    const source = snapshot.assets && snapshot.assets.source;
    if (source) {
        const r = assetRef(uid, id, 'source');
        await uploadBytes(r, source, { contentType: source.type || 'application/octet-stream' });
        out.sourcePath = r.fullPath;
    }

    const payload = snapshot.assets && snapshot.assets.payload;
    if (payload) {
        const r = assetRef(uid, id, 'payload.json');
        await uploadBytes(r, payload, { contentType: 'application/json' });
        out.payloadPath = r.fullPath;
    }

    if (snapshot.thumbnailBlob) {
        const r = assetRef(uid, id, 'thumb.jpg');
        await uploadBytes(r, snapshot.thumbnailBlob, { contentType: 'image/jpeg' });
        out.thumbPath = r.fullPath;
        out.thumbUrl = await getDownloadURL(r);
    }

    return out;
}

const baseDoc = (snapshot, name, assets) => ({
    type: snapshot.type,
    name,
    breadcrumb: snapshot.breadcrumb || [],
    settings: snapshot.settings || {},
    sourcePath: assets.sourcePath,
    payloadPath: assets.payloadPath,
    thumbPath: assets.thumbPath,
    thumbUrl: assets.thumbUrl,
    updatedAt: serverTimestamp(),
});

// Create a brand-new saved map. Returns { id, name }.
export async function createMap(snapshot, name) {
    const uid = requireUid();
    const docRef = doc(mapsCol(uid)); // client-generated id so Storage paths can use it
    const assets = await writeAssets(uid, docRef.id, snapshot);
    await setDoc(docRef, { ...baseDoc(snapshot, name, assets), createdAt: serverTimestamp() });
    return { id: docRef.id, name };
}

// Overwrite an existing saved map in place. Returns { id, name }.
export async function updateMap(id, snapshot, name) {
    const uid = requireUid();
    const docRef = doc(mapsCol(uid), id);
    const assets = await writeAssets(uid, id, snapshot);
    await setDoc(docRef, baseDoc(snapshot, name, assets), { merge: true });
    return { id, name };
}

// List the signed-in user's maps, newest first. Lightweight — no heavy assets.
export async function listMaps() {
    const uid = requireUid();
    const snap = await getDocs(query(mapsCol(uid), orderBy('updatedAt', 'desc')));
    return snap.docs.map((d) => {
        const data = d.data();
        return {
            id: d.id,
            type: data.type,
            name: data.name,
            breadcrumb: data.breadcrumb || [],
            thumbUrl: data.thumbUrl || null,
            updatedAt: data.updatedAt || null,
        };
    });
}

// Load one map fully, resolving heavy assets back into usable form:
//   sourceDataUrl — the source asset as a data URL (untainted; safe for canvas)
//   payload       — parsed JSON payload
export async function getMapForReopen(id) {
    const uid = requireUid();
    const snap = await getDoc(doc(mapsCol(uid), id));
    if (!snap.exists()) throw new Error('That saved map could not be found.');
    const data = snap.data();

    let sourceDataUrl = null;
    if (data.sourcePath) {
        const blob = await getBlob(ref(storage, data.sourcePath));
        sourceDataUrl = await blobToDataUrl(blob);
    }

    let payload = null;
    if (data.payloadPath) {
        const blob = await getBlob(ref(storage, data.payloadPath));
        payload = JSON.parse(await blob.text());
    }

    return {
        id: snap.id,
        type: data.type,
        name: data.name,
        settings: data.settings || {},
        sourceDataUrl,
        payload,
    };
}

export async function renameMap(id, name) {
    const uid = requireUid();
    await setDoc(doc(mapsCol(uid), id), { name, updatedAt: serverTimestamp() }, { merge: true });
}

export async function deleteMap(id) {
    const uid = requireUid();
    const snap = await getDoc(doc(mapsCol(uid), id));
    if (snap.exists()) {
        const data = snap.data();
        for (const path of [data.sourcePath, data.payloadPath, data.thumbPath]) {
            if (path) {
                try {
                    await deleteObject(ref(storage, path));
                } catch (_) {
                    // Best-effort: a missing object shouldn't block deleting the record.
                }
            }
        }
    }
    await deleteDoc(doc(mapsCol(uid), id));
}
